<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { isDomain } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useKontaCompaniesStore } from 'dashboard/stores/kontaCompanies';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const kontaCompaniesStore = useKontaCompaniesStore();

const dialogRef = ref(null);

const state = reactive({
  id: null,
  name: '',
  domain: '',
  description: '',
});

const rules = {
  name: { required },
  domain: { isDomain },
};

const v$ = useVuelidate(rules, state);

const isEditing = computed(() => !!state.id);
const uiFlags = computed(() => kontaCompaniesStore.getUIFlags);
const isSaving = computed(
  () => uiFlags.value.creatingItem || uiFlags.value.updatingItem
);

const dialogTitle = computed(() =>
  isEditing.value
    ? t('KONTA_COMPANIES.FORM.TITLE_EDIT')
    : t('KONTA_COMPANIES.FORM.TITLE_CREATE')
);

const nameError = computed(() =>
  v$.value.name.$error ? t('KONTA_COMPANIES.FORM.NAME.REQUIRED') : ''
);

const domainError = computed(() =>
  v$.value.domain.$error ? t('KONTA_COMPANIES.FORM.DOMAIN.INVALID') : ''
);

const resetState = (company = null) => {
  Object.assign(state, {
    id: company?.id ?? null,
    name: company?.name ?? '',
    domain: company?.domain ?? '',
    description: company?.description ?? '',
  });
  v$.value.$reset();
};

const open = (company = null) => {
  resetState(company);
  dialogRef.value?.open();
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const payload = {
    name: state.name,
    domain: state.domain,
    description: state.description,
  };

  try {
    if (isEditing.value) {
      await kontaCompaniesStore.update({ id: state.id, ...payload });
      useAlert(t('KONTA_COMPANIES.FORM.UPDATE_SUCCESS'));
    } else {
      await kontaCompaniesStore.create(payload);
      useAlert(t('KONTA_COMPANIES.FORM.CREATE_SUCCESS'));
    }
    dialogRef.value?.close();
    emit('saved');
  } catch (error) {
    useAlert(t('KONTA_COMPANIES.FORM.ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :confirm-button-label="t('KONTA_COMPANIES.FORM.SAVE')"
    :cancel-button-label="t('KONTA_COMPANIES.FORM.CANCEL')"
    :is-loading="isSaving"
    :disable-confirm-button="v$.$invalid"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('KONTA_COMPANIES.FORM.NAME.LABEL')"
        :placeholder="t('KONTA_COMPANIES.FORM.NAME.PLACEHOLDER')"
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
        @blur="v$.name.$touch"
      />
      <Input
        v-model="state.domain"
        :label="t('KONTA_COMPANIES.FORM.DOMAIN.LABEL')"
        :placeholder="t('KONTA_COMPANIES.FORM.DOMAIN.PLACEHOLDER')"
        :message="domainError"
        :message-type="domainError ? 'error' : 'info'"
        @blur="v$.domain.$touch"
      />
      <TextArea
        v-model="state.description"
        :label="t('KONTA_COMPANIES.FORM.DESCRIPTION.LABEL')"
        :placeholder="t('KONTA_COMPANIES.FORM.DESCRIPTION.PLACEHOLDER')"
      />
    </div>
  </Dialog>
</template>
